import * as React from 'react';
import * as I18n from 'i18n-js';
import { CoreBumpRecommendationListingCard } from '../../schemas/rql';
import SanitizedRender from '@reverbdotcom/commons/dist/components/sanitized_render';
import { MIN_BUMP_AMOUNT_PERCENT, BUMP_INCREMENT_AMOUNT_PERCENT, MAX_BUMP_AMOUNT_PERCENT } from '../shared/constants';
import { useUser } from '@reverbdotcom/commons/dist/user_hooks';
import { shouldGetBuyBoxRecommendation, fixPrecision } from './bump_helpers';
import { ButtonGroup } from './button_group';
import { RecommendationHighlight } from './recommendation_hightlight';
import { CustomSelect } from './custom_select';
import { Button } from './button';
import { IUser } from '@reverbdotcom/commons/dist/components/user_context_provider';
import AlertBox from '@reverbdotcom/commons/dist/components/alert_box';

const NUMBER_OF_BUTTONS = 5;

interface Props {
  bid: number;
  listing: CoreBumpRecommendationListingCard.Fragment;
  recommendationAmount: number;
  selectedBid: number;
  handleBidChange: (bid: number) => void;
  updateBid: (
    bid: number,
    bumpRecommendationAmount: number,
    listing: CoreBumpRecommendationListingCard.Fragment,
    user: Partial<IUser>,
  ) => void;
}

// given a recommendation amount we want to find the amount to give the
// first (left-most) button in the 5 button group
// for example given a recommendation of 3.5% we would have buttons:
// [2.5, 3, 3.5, 4, 4.5]
//
// So the _starting_ value will be the recommendationAmount - 1. In the above case 3.5 - 1 = 2.5
// However we should make sure that it is never lower than 0.5%
//
// For a larger case where a recommendation is 29.5% we have to account for our upper limit of 30%, so the buttons should be:
// [28, 28.5, 29, 29.5, 30]
// We have to make sure we don't go past 30% on the right side.
//
// So, we want to clamp this number between 0.5% and 28%.
function startAmountPercent(
  recommendationAmount: number,
): number {

  const firstButtonAmount = recommendationAmount - 1;
  const firstButtonMaxAmount = MAX_BUMP_AMOUNT_PERCENT - 2;

  if (firstButtonAmount < MIN_BUMP_AMOUNT_PERCENT) {
    return MIN_BUMP_AMOUNT_PERCENT;
  }

  if (firstButtonAmount > firstButtonMaxAmount) {
    return firstButtonMaxAmount;
  }

  return firstButtonAmount;
}

function recommendationPercents(
  recommendationAmount: number,
): number[] {
  const startPercent = startAmountPercent(recommendationAmount);

  return [...Array(NUMBER_OF_BUTTONS)].map((_, index) => {
    return fixPrecision(startPercent + (index * BUMP_INCREMENT_AMOUNT_PERCENT));
  });
}

function recommendationText(
  recommendationAmount: number,
  listing: CoreBumpRecommendationListingCard.Fragment,
) {
  if (shouldGetBuyBoxRecommendation(listing)) {
    return I18n.t('discovery.bumpModalV2.buyBoxRecommendation', {
      tagOpen: '<span class="weight-bold">',
      tagClose: '</span>',
      bumpPercent: fixPrecision(recommendationAmount),
    });
  }

  return I18n.t('discovery.bumpModalV2.percentileRecommendation', {
    bumpPercent: fixPrecision(recommendationAmount),
    percentile: 85,
    tagOpen: '<span class="weight-bold">',
    tagClose: '</span>',
  });
}

function customSelectedDefault(
  selectedBid: number,
  recommendationPercentsArray: number[],
): boolean {
  if (!selectedBid) {
    return false;
  }

  // selectedBid is returned as a decimal so we should convert to percent to compare with startAmountPercent
  const selectedBidPercent = fixPrecision(selectedBid * 100);

  return selectedBidPercent < recommendationPercentsArray[0] || selectedBidPercent > recommendationPercentsArray[recommendationPercentsArray.length - 1];
}

export function ButtonDisplayRecommendations(props: Props) {
  const user = useUser();

  const [customSelected, setCustomSelected] = React.useState(
    customSelectedDefault(
      props.selectedBid,
      recommendationPercents(
        props.recommendationAmount,
      ),
    ),
  );

  const handleCustomButtonClick = (): void => {
    setCustomSelected(true);
    props.handleBidChange(props.selectedBid);
    props.updateBid(
      props.selectedBid,
      props.recommendationAmount,
      props.listing,
      user,
    );
  };

  const handleButtonClick = (buttonValue): void => {
    props.handleBidChange(buttonValue);
    props.updateBid(
      buttonValue,
      props.recommendationAmount,
      props.listing,
      user,
    );
    setCustomSelected(false);
  };

  const handleCustomSelectChange = (event) => {
    const bid = event?.target?.value;
    props.handleBidChange(bid);
    props.updateBid(
      bid,
      props.recommendationAmount,
      props.listing,
      user,
    );
  };

  return (
    <div className="bump-split-button">
      <div className="bump-split-button__label">
        {I18n.t('discovery.bumpModalV2.buttonLabel')}
      </div>
      <div className="bump-split-button__options">
        <Button
          text={I18n.t('discovery.bumpModalV2.customButton')}
          selected={customSelected}
          onClick={handleCustomButtonClick}
        >
          <CustomSelect
            customSelected={customSelected}
            value={props.selectedBid || props.recommendationAmount}
            onChange={handleCustomSelectChange}
          />
        </Button>
        <ButtonGroup
          customSelected={customSelected}
          selectedBid={props.selectedBid}
          recommendationAmount={props.recommendationAmount}
          recommendationPercents={
            recommendationPercents(
              props.recommendationAmount,
            )
          }
          onClick={handleButtonClick}
        />
      </div>
      <>
        <RecommendationHighlight
          recommendationAmount={props.recommendationAmount}
          recommendationPercents={
            recommendationPercents(
              props.recommendationAmount,
            )
          }
        />
        <AlertBox color="blue" small>
          <SanitizedRender
            html={recommendationText(props.recommendationAmount, props.listing)}
            htmlTag="div"
          />
        </AlertBox>
      </>
    </div>
  );
}



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/discovery-ui/src/components/bump/button_display_recommendations.tsx